// pipe: frontend wave (F6) — storefront Product-detail container. Wires the
// anonymous public detail read (`GET /api/store/listings/:id` — 404 for a
// draft/sold/missing listing, no oracle) + the cart to the pure
// ProductDetailView. All fetch/state/effect plumbing lives HERE.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { hasLine } from '../services/cart';
import { useCart } from '../services/cart-provider';
import type { StorefrontRecord } from '../services/storefront-api';
import { useStorefrontApi } from '../services/storefront-api-provider';
import { ProductDetailView } from './product-detail-view';

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return v === undefined || v === null ? null : String(v);
}

export function ProductDetail() {
  const api = useStorefrontApi();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const cart = useCart();
  const [row, setRow] = useState<StorefrontRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    api
      .browseListing(id)
      .then(r => {
        if (!cancelled) {
          setRow(r);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) {
          return;
        }
        // A 404 is "not available" (sold/delisted/missing — the server is
        // deliberately uniform); anything else is a real error.
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.endsWith(': 404')) {
          setNotFound(true);
        } else {
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const onAddToCart = useCallback(() => {
    if (!row) {
      return;
    }
    cart.add({
      id: row.id,
      headline: str(row.data.headline) ?? row.title,
      ask_price: num(row.data.ask_price),
      currency: str(row.data.currency),
    });
  }, [cart, row]);

  const onGoToCart = useCallback(() => navigate('/cart'), [navigate]);
  const onBack = useCallback(() => navigate('/'), [navigate]);

  return (
    <ProductDetailView
      row={row}
      loading={loading}
      notFound={notFound}
      error={error}
      inCart={row ? hasLine(cart.lines, row.id) : false}
      onAddToCart={onAddToCart}
      onGoToCart={onGoToCart}
      onBack={onBack}
    />
  );
}
